import usePostFileForm, { PostFileFormModelParams } from '@casops/hooks/usePostFileForm';
import { casOpsApi, QueryTag } from '@casops/store/api';
import { FileUploadModel } from '@casops/store/apis/commonModels';
import { useAppDispatch } from '@casops/store/hooks';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
import { zodResolver } from '@hookform/resolvers/zod';
import { TagDescription } from '@reduxjs/toolkit/query';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { DefaultValues, useForm, UseFormResetField, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z, ZodTypeAny } from 'zod';
import { ModelFormControlProps } from '../model-form/ModelForm';
import { ServerErrorFields, SuccessNotificationContext } from '@dmc/ui';

export interface UseFileUploadProps<
  TFormModel extends FileUploadModel,
  TSuccess extends object = never,
> {
  endpointUrl: string; // Must be a relative path to work with API URLs that use additional path segments after the host.
  SuccessWithFailuresResponseGuard?: (data: unknown) => data is TSuccess;
  SuccessWithFailuresResponseMessage?: (data: TSuccess) => boolean;
  SuccessWithFailuresResetNotification?: () => void;
  tagsToClear: TagDescription<QueryTag>[];
  defaultValues: DefaultValues<TFormModel>;
  successMessage: string;
  fieldsSchema?: ZodTypeAny;
  isFileUpload?: boolean;
  validateOnLoad?: boolean;
  serverErrorFields?: ServerErrorFields<TFormModel>;
}

interface UseModelFormReturn<TFormModel extends object> extends ModelFormControlProps<TFormModel> {
  resetField: UseFormResetField<TFormModel>;
  percentage: number;
  setValue: UseFormSetValue<TFormModel>;
}

export const useFileUploadForm = <
  TFormModel extends FileUploadModel,
  TSuccess extends object = never,
>({
  endpointUrl,
  tagsToClear,
  defaultValues,
  successMessage,
  fieldsSchema,
  isFileUpload = true,
  validateOnLoad = false,
  serverErrorFields,
  SuccessWithFailuresResponseGuard,
  SuccessWithFailuresResponseMessage,
  SuccessWithFailuresResetNotification,
}: UseFileUploadProps<TFormModel, TSuccess>): UseModelFormReturn<TFormModel> => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const schema = useMemo(() => {
    const baseSchema = isFileUpload
      ? z.object({
          file: z.instanceof(File, { message: t('Validation.SpecifyFile') }),
        })
      : z.object({});

    if (fieldsSchema) {
      if (fieldsSchema instanceof z.ZodObject) {
        return baseSchema.merge(fieldsSchema);
      } else {
        // Fallback: just use .and for any other ZodType
        return baseSchema.and(fieldsSchema);
      }
    }

    return baseSchema;
  }, [isFileUpload, t, fieldsSchema]);

  const {
    handleSubmit,
    control,
    resetField,
    formState: { isSubmitting, dirtyFields, isValid },
    trigger,
    setValue,
    setError,
  } = useForm<TFormModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  // Add useEffect to trigger validation when item data loads
  useEffect(() => {
    if (validateOnLoad) {
      trigger(); // Trigger validation for all fields
    }
  }, [trigger, validateOnLoad, schema]);

  // Don't use the isDirty flag with ReactHookForm - see https://github.com/react-hook-form/react-hook-form/issues/3213.
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;
  const canSubmit = !isSubmitting && isValid && isAnyFieldDirty;

  const serverErrorProps = useMemo<PostFileFormModelParams['serverErrorProps']>(() => {
    return serverErrorFields ? { serverErrorFields, setError } : undefined;
  }, [serverErrorFields, setError]);

  const { errorMessage, upload, percentage } = usePostFileForm({
    url: endpointUrl,
    serverErrorProps,
  });

  const onSubmitInternal = useCallback(
    async (data: TFormModel) => {
      const { file, ...withoutFile } = data;
      typeof SuccessWithFailuresResetNotification === 'function' &&
        SuccessWithFailuresResetNotification();
      const result = await upload(withoutFile, data.file);

      if (!result?.success) return;

      if (
        SuccessWithFailuresResponseGuard?.(result.data) === true &&
        typeof SuccessWithFailuresResponseMessage === 'function'
      ) {
        const showToast = SuccessWithFailuresResponseMessage(result.data);
        dispatch(casOpsApi.util.invalidateTags(tagsToClear));
        if (showToast) {
          showSuccessMessage(successMessage);
          dispatch(closeSidePanel());
        }
        return;
      }

      // Default success path when guard not provided
      showSuccessMessage(successMessage);
      dispatch(casOpsApi.util.invalidateTags(tagsToClear));
      dispatch(closeSidePanel());
    },
    [
      SuccessWithFailuresResetNotification,
      upload,
      SuccessWithFailuresResponseGuard,
      SuccessWithFailuresResponseMessage,
      showSuccessMessage,
      successMessage,
      dispatch,
      tagsToClear,
    ],
  );

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  return {
    control,
    onSubmit,
    canSubmit,
    errorMessage,
    resetField,
    percentage,
    setValue,
  };
};

export default useFileUploadForm;
